import React, { useEffect, useState } from "react";
import axios from "axios";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import { useHistory, useParams, withRouter } from "react-router-dom";
import {
    BUTTON_VARIANTS,
    Button,
    CardBody,
    CardWrapper,
    Dialog,
    ICON_SIZE,
    IconClose,
    Loader,
    MENU_PLACEMENT,
    Switch,
    TOAST_POSITIONS,
    TOAST_VARIANTS,
    TOAST_VISIBILITY_DURATIONS,
    Toast
} from "cdk-radial";
import config from "../config/app.conf.json";
import withNavigation from "../components/withNavigation";
import AsyncTypeaheadMenu from "../components/radial/AsyncTypeaheadMenu";
import {
    buildDisabledFeatures,
    buildSaveRequests,
    getChangedFunctions,
    isFeatureEnabled,
    sendSaveRequests
} from "./disableFunctionsApi";
import "./DisableFunctions.css";

const FUNCTIONS = {
    ORG_LEVEL: {
        CREATE_NEW_APPS: {
            feature: "CREATE_NEW_APPS",
            name: "Create New Apps",
            description: "Allows developers in this organization to create and register new apps in the Developer Network or API Directory.",
            confirmationMessage: "Developers will be able to create and register new apps again.",
            disableConfirmationMessage: "Developers will not be able to create or register new apps."
        },
    },
    APP_LEVEL: {
        CERTIFY_APP: {
            feature: "CERTIFY_APP",
            name: "Certify App",
            description: "Gives developers the ability to submit apps for certification within their developer account.",
            confirmationMessage: "Developers will be able to certify apps in their developer account.",
            disableConfirmationMessage: "Developers will not be able to certify the selected app."
        },
        NEW_DEALER_ENROLMENT: {
            feature: "NEW_DEALER_ENROLMENT",
            name: "New Dealer Enrollments",
            description: "Allows the organization to activate new dealer accounts directly from the Marketplace.",
            confirmationMessage: "Users will be able to activate new dealer accounts for the selected app in the Marketplace.",
            disableConfirmationMessage: "Users will not be able to activate new dealer accounts for the selected app in the Marketplace."
        },
        ADD_NEW_APIS: {
            feature: "ADD_NEW_APIS",
            name: "Add New APIs",
            description: "Enables users to add or modify API in their apps from the API Directory or during app editing.",
            confirmationMessage: "Users will be able to add or modify API contracts for the selected app.",
            disableConfirmationMessage: "Users will not be able to add or modify API contracts for the selected app."
        },
        API_CALLS: {
            feature: "API_TRANSACTIONS",
            name: "API Calls",
            description: "Allows all API calls included in the organization’s apps.",
            confirmationMessage: "Fortellis will allow all API calls from the selected apps under this org.",
            disableConfirmationMessage: "Fortellis will block all API calls from the selected app."
        }
    },
}

const getChangeMessage = (func) =>
    `${func.enabled ? "Enable" : "Disable"} - ${func.enabled ? func.confirmationMessage : func.disableConfirmationMessage}`;

const ConfirmationDialogBody = ({ orgFunctions, appChanges, saveError }) => {
    const hasChanges = orgFunctions.length > 0 || appChanges.length > 0;

    return (
        <>
            <p>
                You are about to change the following permissions for this organization:
            </p>
            {hasChanges ? (
                <ol className="disable-functions__confirmation-groups">
                    {orgFunctions.length > 0 && (
                        <li>
                            <strong>Org Level Functions</strong>
                            <ul>
                                {orgFunctions.map((func) => (
                                    <li key={func.functionId}>
                                        {func.name}: {getChangeMessage(func)}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                    {appChanges.map(({ appId, appName, functions }) => (
                        <li key={appId}>
                            <strong>App Level Functions for '{appName}'</strong>
                            <ul>
                                {functions.map((func) => (
                                    <li key={func.functionId}>
                                        {func.name}: {getChangeMessage(func)}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ol>
            ) : (
                <p>No function changes have been selected.</p>
            )}
            {saveError && (
                <Toast
                    content="Unable to save function changes. Please try again."
                    dataTestId="function-changes-error-toast"
                    id="function-changes-error-toast"
                    position={TOAST_POSITIONS.INLINE}
                    variant={TOAST_VARIANTS.NEGATIVE}
                    visibilityDuration={TOAST_VISIBILITY_DURATIONS.INFINITE}
                />
            )}
        </>
    );
};

const DisableFunctions = ({ auth }) => {
    const history = useHistory();
    const { orgId } = useParams();
    const { accessToken } = auth;
    const [orgData, setOrgData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [checkedOrgFunctions, setCheckedOrgFunctions] = useState({});
    const [checkedAppFunctions, setCheckedAppFunctions] = useState({});
    const [solutions, setSolutions] = useState([]);
    const [solutionsLoading, setSolutionsLoading] = useState(false);
    const [solutionsError, setSolutionsError] = useState(false);
    const [selectedSolution, setSelectedSolution] = useState();
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);

    const searchSolutions = (searchText) => {
        const query = searchText.trim().toLowerCase();
        return Promise.resolve(
            solutions
                .map((solution) => ({
                    label: solution.displayName || solution.name || solution.id,
                    value: solution.id
                }))
                .filter(({ label, value }) =>
                    label.toLowerCase().includes(query) || String(value).toLowerCase().includes(query)
                )
        );
    };

    const handleSolutionChange = (solution) => {
        if (solution?.value !== selectedSolution?.value) {
            setCheckedAppFunctions({});
        }
        setSelectedSolution(solution);
    };

    const handleOrgFunctionChange = (event, functionId) => {
        const { checked } = event.target;
        setCheckedOrgFunctions((previous) => ({
            ...previous,
            [functionId]: checked
        }));
    };

    const handleAppFunctionChange = (event, functionId) => {
        const appId = selectedSolution?.value;
        if (!appId) {
            return;
        }
        const { checked } = event.target;
        setCheckedAppFunctions((previous) => ({
            [appId]: {
                ...previous[appId],
                [functionId]: checked
            }
        }));
    };

    const selectedSolutionData = solutions.find((app) => app.id === selectedSolution?.value);
    const changedOrgFunctions = getChangedFunctions(
        FUNCTIONS.ORG_LEVEL,
        checkedOrgFunctions,
        orgData?.disabledFeatures
    );
    const changedApps = Object.entries(checkedAppFunctions)
        .map(([appId, enabledState]) => {
            const solution = solutions.find((app) => app.id === appId);
            return {
                appId,
                appName: solution?.displayName || solution?.name || appId,
                functions: getChangedFunctions(FUNCTIONS.APP_LEVEL, enabledState, solution?.disabledFeatures)
            };
        })
        .filter((app) => app.functions.length > 0);
    const hasFunctionChanges = changedOrgFunctions.length > 0 || changedApps.length > 0;

    const closeConfirmation = () => {
        if (isSaving) {
            return;
        }
        setSaveError(false);
        setIsConfirmationOpen(false);
    };

    const handleSave = async () => {
        if (!hasFunctionChanges) {
            return;
        }

        const orgDisabledFeatures = changedOrgFunctions.length > 0
            ? buildDisabledFeatures(orgData?.disabledFeatures, FUNCTIONS.ORG_LEVEL, checkedOrgFunctions)
            : null;
        const appUpdates = changedApps.map(({ appId }) => ({
            appId,
            disabledFeatures: buildDisabledFeatures(
                solutions.find((app) => app.id === appId)?.disabledFeatures,
                FUNCTIONS.APP_LEVEL,
                checkedAppFunctions[appId]
            )
        }));

        setIsSaving(true);
        setSaveError(false);
        setShowSuccessToast(false);
        try {
            await sendSaveRequests(
                buildSaveRequests({ orgId, orgDisabledFeatures, appUpdates, changedApps }),
                accessToken
            );

            if (orgDisabledFeatures) {
                setOrgData((previous) => ({ ...previous, disabledFeatures: orgDisabledFeatures }));
            }
            setSolutions((previous) => previous.map((app) => {
                const update = appUpdates.find(({ appId }) => appId === app.id);
                return update ? { ...app, disabledFeatures: update.disabledFeatures } : app;
            }));
            setCheckedOrgFunctions({});
            setCheckedAppFunctions({});
            setIsConfirmationOpen(false);
            setShowSuccessToast(true);
        } catch (error) {
            console.error("Error saving function changes:", error);
            setSaveError(true);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (!accessToken) {
            return undefined;
        }

        let isCurrent = true;
        axios
            .get(`${config.api.organizations_uri}/${orgId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
            .then(({ data }) => {
                if (isCurrent) {
                    setOrgData(data || "");
                }
            })
            .catch((error) => {
                console.error("Error fetching organization details:", error);
                if (isCurrent) {
                    setLoadError(true);
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [accessToken, orgId]);

    useEffect(() => {
        if (!accessToken) {
            return undefined;
        }

        let isCurrent = true;
        setSolutionsLoading(true);
        axios
            .get(`${config.api.solutions_url}?includeTest=false&orgId=${encodeURIComponent(orgId)}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: "application/json"
                }
            })
            .then(({ data }) => {
                if (isCurrent) {
                    const solutionList = Array.isArray(data) ? data : data?.solutions;
                    setSolutions(Array.isArray(solutionList) ? solutionList : []);
                }
            })
            .catch((error) => {
                console.error("Error fetching organization solutions:", error);
                if (isCurrent) {
                    setSolutionsError(true);
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setSolutionsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [accessToken, orgId]);

    return (
        <div className="disable-functions__page">
            <CardWrapper style={{ marginBottom: "32px", marginTop: "32px" }}>
                <CardBody>
                    <div>
                        <h2>Disable Functions</h2>
                        <p>Adjust the functionality settings for AR issues</p>
                    </div>
                </CardBody>
            </CardWrapper>
            <CardWrapper>
                <CardBody>
                    {
                        isLoading ? <Loader label="Loading organization details..." variant="inline" /> : loadError ? <p>Unable to load organization details.</p> : null
                    }
                    {!isLoading && !loadError && (
                        <section className="disable-functions__content">
                            <section className="disable-functions__org-details">
                                <div className="heading">{orgData?.name || 'N/A'}</div>
                                <div className="disable-functions__org-detail-row">
                                    <div className="key">Organization ID:</div><div className="value">{orgId}</div>
                                </div>
                                <div className="disable-functions__org-detail-row">
                                    <div className="key">Website:</div><div className="value">{orgData?.website || 'N/A'}</div>
                                </div>
                            </section>
                            <section className="disable-functions__group">
                                <div className="disable-functions__group-title">Org Level Functions</div>
                                <hr />
                                {
                                    Object.entries(FUNCTIONS.ORG_LEVEL).map(([functionId, func]) => (
                                        <div className="disable-functions__function-row" key={functionId}>
                                            <div className="disable-functions__function-copy">
                                                <div className="disable-functions__function-name">{func.name}</div>
                                                <div className="disable-functions__function-description">{func.description}</div>
                                            </div>
                                            <div className="disable-functions__switch-control">
                                                <Switch
                                                    aria-label={func.name}
                                                    checked={checkedOrgFunctions[functionId] ?? isFeatureEnabled(orgData?.disabledFeatures, func.feature)}
                                                    label=""
                                                    name={`org-function-${functionId}`}
                                                    onChange={(event) => handleOrgFunctionChange(event, functionId)}
                                                />
                                            </div>
                                        </div>
                                    ))
                                }
                                <section className="disable-functions__group">
                                    <div className="disable-functions__app-level-header">
                                        <div className="disable-functions__group-title">App Level Functions</div>
                                        <div className="disable-functions__solution-dropdown">
                                            <AsyncTypeaheadMenu
                                                dataTestId="disable-functions-solution"
                                                fetchOptions={searchSolutions}
                                                id="disable-functions-solution"
                                                label="Select an app to manage function access."
                                                name="disable-functions-solution"
                                                onClear={() => handleSolutionChange(undefined)}
                                                onSelect={handleSolutionChange}
                                                placement={MENU_PLACEMENT.BOTTOM_START}
                                                placeholder={
                                                    solutionsLoading
                                                        ? "Loading solutions..."
                                                        : "Type to search apps"
                                                }
                                            />
                                        </div>
                                    </div>
                                    {solutionsError && <p>Unable to load solutions.</p>}
                                    <hr />
                                    {
                                        Object.entries(FUNCTIONS.APP_LEVEL).map(([functionId, func]) => (
                                            <div className="disable-functions__function-row" key={functionId}>
                                                <div className="disable-functions__function-copy">
                                                    <div className="disable-functions__function-name">{func.name}</div>
                                                    <div className="disable-functions__function-description">{func.description}</div>
                                                </div>
                                                <div className="disable-functions__switch-control">
                                                    <Switch
                                                        aria-label={func.name}
                                                        checked={
                                                            Boolean(selectedSolutionData) &&
                                                            (checkedAppFunctions[selectedSolutionData.id]?.[functionId] ??
                                                                isFeatureEnabled(selectedSolutionData.disabledFeatures, func.feature))
                                                        }
                                                        isDisabled={!selectedSolution?.value}
                                                        label=""
                                                        name={`app-function-${selectedSolution?.value || "unselected"}-${functionId}`}
                                                        onChange={(event) => handleAppFunctionChange(event, functionId)}
                                                    />
                                                </div>
                                            </div>
                                        ))
                                    }
                                </section>
                                <hr />
                                <section className="disable-functions__action-buttons">
                                    <Button
                                        onClick={() => history.push("/org-management")}
                                        text="Cancel"
                                        variant="text"
                                    />
                                    <Button
                                        isDisabled={!hasFunctionChanges}
                                        onClick={() => setIsConfirmationOpen(true)}
                                        text="Save Changes"
                                    />
                                </section>
                            </section>
                        </section>
                    )}
                </CardBody>
            </CardWrapper>
            <Dialog
                buttonsProps={[
                    {
                        dataTestId: "function-changes-cancel",
                        id: "function-changes-cancel",
                        onClick: closeConfirmation,
                        text: "Cancel",
                        variant: BUTTON_VARIANTS.TEXT
                    },
                    {
                        dataTestId: "function-changes-confirm",
                        id: "function-changes-confirm",
                        isDisabled: isSaving || saveError || !hasFunctionChanges,
                        onClick: handleSave,
                        text: isSaving ? "Saving..." : "Confirm",
                        variant: BUTTON_VARIANTS.PRIMARY
                    }
                ]}
                dataTestId="function-changes-confirmation-dialog"
                id="function-changes-confirmation-dialog"
                isOpen={isConfirmationOpen}
                onClose={closeConfirmation}
                title="Confirm Function Changes"
            >
                <ConfirmationDialogBody
                    orgFunctions={changedOrgFunctions}
                    appChanges={changedApps}
                    saveError={saveError}
                />
            </Dialog>
            {showSuccessToast && (
                <Toast
                    actions={[
                        {
                            icon: <IconClose />,
                            iconSize: ICON_SIZE.SMALL,
                            onClick: () => setShowSuccessToast(false),
                            text: "Dismiss",
                            variant: BUTTON_VARIANTS.ICON
                        }
                    ]}
                    content="Function changes saved successfully."
                    dataTestId="function-changes-success-toast"
                    id="function-changes-success-toast"
                    onVisibilityDurationEnd={() => setShowSuccessToast(false)}
                    position={TOAST_POSITIONS.FIXED}
                    variant={TOAST_VARIANTS.POSITIVE}
                    visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
                />
            )}
        </div>
    );
};

const withDisableFunctionsNavigation = withNavigation([
    { text: "Back to Home", goTo: "/" },
    { text: "Back to Search Orgs", goTo: "/org-management" }
]);

export default withAuth(withRouter(withDisableFunctionsNavigation(DisableFunctions)));

